import React from 'react';
import { IconCollection } from '../../../components';
import css from '../EventDashboardPageForm.module.css';

const getTransitionCreatedAt = (txTransitions, transitionName) => {
  const found = Array.isArray(txTransitions) ? txTransitions.find(t => t?.transition === transitionName) : null;
  return found?.createdAt ? new Date(found.createdAt) : null;
};

const formatDateTime = date => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString()} at ${d.toLocaleTimeString()}`;
};

const getEventCompletedAt = txAttrs => {
  const txTransitions = txAttrs?.transitions || [];
  return (
    getTransitionCreatedAt(txTransitions, 'transition/accept-deliverable') ||
    getTransitionCreatedAt(txTransitions, 'transition/provider-complete') ||
    getTransitionCreatedAt(txTransitions, 'transition/confirm-payment') ||
    null
  );
};

const EventTimeline = props => {
  const { transaction, isProvider = false } = props;

  const txAttrs = transaction?.attributes;
  const txState = txAttrs?.state;

  // Resolve the invoice type (overtime / expense / reduction) from either data shape.
  const getInvoiceType = () => {
    const pdA = transaction?.attributes?.protectedData || {};
    const pdB = transaction?.protectedData || {};
    const invoiceType =
      pdA?.invoiceData?.invoiceType ||
      pdB?.invoiceData?.invoiceType ||
      pdA?.invoiceType ||
      pdB?.invoiceType;
    if (invoiceType) return invoiceType;
    if (pdA?.isOvertimeInvoice || pdB?.isOvertimeInvoice) return 'overtime';
    if (pdA?.isExpenseInvoice || pdB?.isExpenseInvoice) return 'expense';
    if (pdA?.isReductionInvoice || pdB?.isReductionInvoice) return 'reduction';
    if (pdA?.invoiceData?.overtimeDetails || pdB?.invoiceData?.overtimeDetails) return 'overtime';
    if (pdA?.invoiceData?.reductionDetails || pdB?.invoiceData?.reductionDetails) return 'reduction';
    return null;
  };
  const invoiceType = getInvoiceType();
  const isInvoiceAdjustment = !!invoiceType;

  // Mirrors AdjustedBill `showLeaveReviewCta` trigger (negotiationState === 'completed')
  // We don't have `stateData.processState` here, so we use the raw tx state.
  const isInReviewableState =
    txState === 'state/completed' ||
    txState === 'state/delivered' ||
    txState === 'state/accepted-deliverable';

  const reviewAlreadyLeftByRole = (() => {
    if (txState === 'state/reviewed') return true;
    if (isProvider) return txState === 'state/reviewed-by-provider';
    return txState === 'state/reviewed-by-customer';
  })();

  const completedAt = getEventCompletedAt(txAttrs);
  const createdAt = txAttrs?.createdAt ? new Date(txAttrs.createdAt) : null;
  const lastTransitionedAt = txAttrs?.lastTransitionedAt ? new Date(txAttrs.lastTransitionedAt) : null;

  const timelineItems = [];

  if (completedAt) {
    timelineItems.push({
      key: 'event-completed',
      iconWrapperClass: css.timelineIconCompleted,
      icon: 'tick_icon',
      status: 'Event Completed',
      date: completedAt,
      description: 'Event successfully completed with all services delivered',
    });
  }

  if (isInvoiceAdjustment) {
    const invoiceTimelineCopy = {
      overtime: { status: 'Overtime Request Reported', description: 'Overtime charges were submitted' },
      expense: { status: 'Expense Coverage Reported', description: 'Additional expenses were submitted' },
      reduction: { status: 'Bill Reduction Reported', description: 'A bill reduction credit was submitted' },
    };
    const copy = invoiceTimelineCopy[invoiceType] || invoiceTimelineCopy.overtime;
    timelineItems.push({
      key: 'invoice-reported',
      iconWrapperClass: css.timelineIconPending,
      icon: 'time_icon',
      status: copy.status,
      date: lastTransitionedAt || createdAt,
      description: copy.description,
    });
  }

  if (timelineItems.length === 0) {
    timelineItems.push({
      key: 'event-created',
      iconWrapperClass: css.timelineIconPending,
      icon: 'time_icon',
      status: 'Event Created',
      date: createdAt,
      description: 'Transaction was created',
    });
  }

  return (
    <div className={css.eventTimelineSection}>
      <h3 className={css.eventTimelineTitle}>Event Timeline</h3>

      {timelineItems.map(item => {
        return (
          <div key={item.key} className={css.eventTimelineItem}>
            <div className={css.timelineIcon}>
              <div className={item.iconWrapperClass}>
                <IconCollection icon={item.icon} />
              </div>
            </div>
            <div className={css.timelineContent}>
              <div className={css.timelineStatus}>{item.status}</div>
              {item.date ? <div className={css.timelineDate}>{formatDateTime(item.date)}</div> : null}
              {item.description ? <div className={css.timelineDescription}>{item.description}</div> : null}
            </div>
          </div>
        );
      })}

      {isInReviewableState && !reviewAlreadyLeftByRole ? (
        <div className={css.eventTimelineItem}>
          <div className={css.timelineIcon}>
            <div className={css.timelineIconWaiting}>
              <IconCollection icon="awaiting_icon" />
            </div>
          </div>
          <div className={css.timelineContent}>
            <div className={css.awaitingText}>Awaiting Review</div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EventTimeline;
